import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import SplitText from 'gsap/SplitText';
gsap.registerPlugin(ScrollTrigger, SplitText);

import { map, lerp, clamp, getMousePos } from '../utils/utils.js';
import { onWidthResize, offWidthResize } from '../utils/resize.js';


export default class DesignersList {
    constructor(container) {
        this.container = container;
        this.title = this.container.querySelector('.designers-list__title');
        this.description = this.container.querySelector('.designers-list__description');
        this.text_lines = new SplitText(this.title, { type: 'lines,words', linesClass: "lineParent" });


        this.DOM = { el: this.container };
        this.DOM.menuItems = this.DOM.el.querySelectorAll('.designer-card');
        this.animatableProperties = {
            tx: { previous: 0, current: 0, amt: 0.06 },
            ty: { previous: 0, current: 0, amt: 0.06 },
            rotation: { previous: 0, current: 0, amt: 0.06 },
        };

        this.menuItems = [];

        [...this.DOM.menuItems].forEach((item, pos) => this.menuItems.push(new MenuItem(item, item.getAttribute('data-img'), pos, this.animatableProperties)));



        this.init();
        this.events();
    }

    init = () => {
        let tl = gsap.timeline({ delay: 0.7 });
        tl.fromTo(this.text_lines.words, { yPercent: 100, }, {
            yPercent: 0, duration: 1, stagger: { each: 0.3, ease: 'power.out', from: 'start' }, ease: 'power3.out',
            onComplete: () => onWidthResize(this.onResize)
        });

        if (this.description) {
            gsap.fromTo(
                this.description,
                { opacity: 0, y: 30 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.8,
                    delay: 1,
                    ease: 'power3.out',
                    // Clear the leftover transform so this wrapper stops being a
                    // stacking context that would trap the open dropdown below the grid.
                    clearProps: 'transform',
                }
            );
        }

        this.menuItems.forEach((menuItem, i) => {
            gsap.fromTo(
                menuItem.DOM.el,
                { opacity: 0, y: 40 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.8,
                    ease: 'power3.out',

                    delay: (i % 3) * 0.1 + 1,
                }
            );
        });

    };

    events = () => {
    };


    onResize = () => {
        this.text_lines.revert();
        offWidthResize(this.onResize);
    }
}


let mousepos = { x: 0, y: 0 };
let mousePosCache = mousepos;
let direction = { x: mousePosCache.x - mousepos.x, y: mousePosCache.y - mousepos.y };
window.addEventListener('mousemove', ev => mousepos = getMousePos(ev));

export class MenuItem {
    constructor(el, image, inMenuPosition, animatableProperties) {
        this.DOM = { el: el };
        this.image = image;
        this.inMenuPosition = inMenuPosition;
        this.animatableProperties = animatableProperties;

        this.layout();
        this.initEvents();
    }

    layout = () => {

        this.DOM.reveal = document.createElement('div');
        this.DOM.reveal.className = 'hover-reveal';

        this.DOM.revealInner = document.createElement('div');
        this.DOM.revealInner.className = 'hover-reveal__inner';
        this.DOM.revealImage = document.createElement('div');
        this.DOM.revealImage.className = 'hover-reveal__img';
        this.DOM.revealImage.style.backgroundImage = `url(${this.image})`;

        this.DOM.revealInner.appendChild(this.DOM.revealImage);
        this.DOM.reveal.appendChild(this.DOM.revealInner);
        this.DOM.el.appendChild(this.DOM.reveal);
    }

    calcBounds = () => {
        this.bounds = {
            el: this.DOM.el.getBoundingClientRect(),
            reveal: this.DOM.reveal.getBoundingClientRect()
        };
    }

    // Matches `.designers-list__grid` 2-col layout (`maxsm` = < 1024px).
    isTwoColumnGrid = () => window.matchMedia('(min-width: 1024px)').matches;

    initEvents = () => {
        this.mouseenterFn = (ev) => {
            // Hover photos are desktop-only; mobile cards match the boxed lists.
            if (!this.isTwoColumnGrid()) return;

            this.showImage();
            this.firstRAFCycle = true;

            this.loopRender();
        };
        this.mouseleaveFn = () => {
            this.stopRendering();

            this.hideImage();
        };

        this.DOM.el.addEventListener('mouseenter', this.mouseenterFn);
        this.DOM.el.addEventListener('mouseleave', this.mouseleaveFn);
    }

    showImage = () => {
        gsap.killTweensOf(this.DOM.revealInner);
        gsap.killTweensOf(this.DOM.revealImage);

        this.tl = gsap.timeline({
            onStart: () => {
                this.DOM.reveal.style.opacity = 1;
                gsap.set(this.DOM.el, { zIndex: 2 });
            }
        })
            .to(this.DOM.revealInner, 0.2, {
                ease: 'Sine.easeOut',
                startAt: { x: direction.x < 0 ? '-100%' : '100%' },
                x: '0%'
            })
            .to(this.DOM.revealImage, 0.2, {
                ease: 'Sine.easeOut',
                startAt: { x: direction.x < 0 ? '100%' : '-100%' },
                x: '0%'
            }, 0);
    }

    hideImage = () => {
        gsap.killTweensOf(this.DOM.revealInner);
        gsap.killTweensOf(this.DOM.revealImage);

        this.tl = gsap.timeline({
            onStart: () => {
                gsap.set(this.DOM.el, { zIndex: 2 });
            },
            onComplete: () => {
                gsap.set(this.DOM.reveal, { opacity: 0 });
                gsap.set(this.DOM.el, { zIndex: 'auto' });
            }
        });
        this.tl.to(this.DOM.revealInner, 0.2, {
            ease: 'Sine.easeOut',
            x: direction.x < 0 ? '100%' : '-100%'
        });
        this.tl.to(this.DOM.revealImage, 0.2, {
            ease: 'Sine.easeOut',
            x: direction.x < 0 ? '-100%' : '100%'
        }, 0);
    }

    loopRender = () => {
        if (!this.requestId) {
            this.requestId = requestAnimationFrame(() => this.render());
        }
    }

    stopRendering = () => {
        if (this.requestId) {
            window.cancelAnimationFrame(this.requestId);
            this.requestId = undefined;
        }
    }

    render = () => {
        this.requestId = undefined;

        if (this.firstRAFCycle) {
            this.calcBounds();
        }

        const mouseDistanceX = clamp(Math.abs(mousePosCache.x - mousepos.x), 0, 100);
        direction = { x: mousePosCache.x - mousepos.x, y: mousePosCache.y - mousepos.y };
        mousePosCache = { x: mousepos.x, y: mousepos.y };

        const cardWidth = this.bounds.el.width;
        const revealWidth = this.bounds.reveal.width;
        const revealHeight = this.bounds.reveal.height;
        const mouseXInCard = mousepos.x - this.bounds.el.left;
        const mouseYInCard = mousepos.y - this.bounds.el.top;

        this.animatableProperties.ty.current = mouseYInCard - revealHeight / 2;

        // Two-col desktop: keep the photo beside the name box, but still
        // chase the pointer (lerp + tilt) around that parked side so it
        // cannot slide fully behind the white hover card.
        if (this.isTwoColumnGrid()) {
            const isLeftColumn = this.inMenuPosition % 2 === 0;
            const gap = 20;
            const t = cardWidth > 0 ? clamp(mouseXInCard / cardWidth, 0, 1) : 0.5;
            const travel = Math.min(160, cardWidth * 0.55);
            const parkedX = isLeftColumn
                ? cardWidth + gap
                : -revealWidth - gap;
            // More travel away from the card than toward it.
            const driftX = isLeftColumn
                ? map(t, 0, 1, -travel * 0.35, travel)
                : map(t, 0, 1, -travel, travel * 0.35);

            this.animatableProperties.tx.current = parkedX + driftX;
        } else {
            this.animatableProperties.tx.current = mouseXInCard - revealWidth / 2;
        }

        const spin = this.firstRAFCycle ? 0 : map(mouseDistanceX, 0, 100, 0, direction.x < 0 ? 80 : -80);
        const lean = map(clamp(mouseXInCard, 0, Math.max(cardWidth, 1)), 0, Math.max(cardWidth, 1), -14, 14);
        this.animatableProperties.rotation.current = spin + lean;


        this.animatableProperties.tx.previous = this.firstRAFCycle ? this.animatableProperties.tx.current : lerp(this.animatableProperties.tx.previous, this.animatableProperties.tx.current, this.animatableProperties.tx.amt);
        this.animatableProperties.ty.previous = this.firstRAFCycle ? this.animatableProperties.ty.current : lerp(this.animatableProperties.ty.previous, this.animatableProperties.ty.current, this.animatableProperties.ty.amt);
        this.animatableProperties.rotation.previous = this.firstRAFCycle ? this.animatableProperties.rotation.current : lerp(this.animatableProperties.rotation.previous, this.animatableProperties.rotation.current, this.animatableProperties.rotation.amt);

        gsap.set(this.DOM.reveal, {
            x: this.animatableProperties.tx.previous,
            y: this.animatableProperties.ty.previous,
            rotation: this.animatableProperties.rotation.previous,
        });


        this.firstRAFCycle = false;
        this.loopRender();
    }
}